import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {getFunctions, httpsCallable} from "firebase/functions";
import {Room, RoomEvent, Track} from "livekit-client";
import controls from "./controls";
import {showSnackbar} from "./Snackbar";
import {isMonitoredCallStillActive} from "./reviewerMonitoringPolicy";
import {isMonitorAccessRevoked} from "./reviewerAccessPolicy";

const functions = getFunctions();
const listMonitorCalls = httpsCallable(functions, "getLiveMonitorCalls");
const createMonitorToken = httpsCallable(
    functions,
    "createCallMonitorToken",
);
const closeMonitorSession = httpsCallable(functions, "endCallMonitoring");

const clean = (value) => String(value || "").trim();
const MONITOR_VOLUME_KEY = "frecall-agent-monitor-volume";
const ACCESS_RETRY_MS = 60000;
const clampVolume = (value) =>
  Math.min(1, Math.max(0, Number(value) || 0));
const readMonitorVolume = () => {
  const stored = window.localStorage.getItem(MONITOR_VOLUME_KEY);
  if (stored === null || stored === "") return 0.8;
  const saved = Number(stored);
  return Number.isFinite(saved) ? clampVolume(saved) : 0.8;
};

export default function useReviewerMonitoring({
  enabled,
  ownCallActive,
  queueEntries = [],
}) {
  const [isReviewer, setIsReviewer] = useState(false);
  const [calls, setCalls] = useState([]);
  const [joiningCallId, setJoiningCallId] = useState("");
  const [listeningCallId, setListeningCallId] = useState("");
  const [listeningCallerId, setListeningCallerId] = useState("");
  const [monitorVolume, setMonitorVolumeState] = useState(readMonitorVolume);
  const stateRef = useRef(null);
  const reviewerRef = useRef(false);
  const accessAttemptedAtRef = useRef(0);
  const queueEntriesRef = useRef(queueEntries);
  const monitorVolumeRef = useRef(monitorVolume);

  useEffect(() => {
    queueEntriesRef.current = queueEntries;
  }, [queueEntries]);

  const stop = useCallback(async (report = true, message = "") => {
    const current = stateRef.current;
    stateRef.current = null;
    setJoiningCallId("");
    setListeningCallId("");
    setListeningCallerId("");
    (current?.audioElements || []).forEach((audio) => {
      audio.pause();
      audio.remove();
    });
    current?.room?.disconnect();
    if (report && current?.monitorSessionId) {
      try {
        await closeMonitorSession({
          monitorSessionId: current.monitorSessionId,
        });
      } catch (_) {
        // The monitor token remains short-lived if audit closure is interrupted.
      }
    }
    if (message) showSnackbar(message);
  }, []);

  const setMonitorVolume = useCallback((value) => {
    const nextVolume = clampVolume(value);
    monitorVolumeRef.current = nextVolume;
    setMonitorVolumeState(nextVolume);
    window.localStorage.setItem(MONITOR_VOLUME_KEY, String(nextVolume));
    (stateRef.current?.audioElements || []).forEach((audio) => {
      audio.volume = nextVolume;
    });
  }, []);

  const refreshCalls = useCallback(async () => {
    if (!enabled || !controls.roomId ||
        !reviewerRef.current ||
        document.visibilityState === "hidden") return;
    try {
      const response = await listMonitorCalls({roomId: controls.roomId});
      const nextCalls = response.data?.calls || [];
      setCalls(nextCalls);
      const current = stateRef.current;
      if (current && !nextCalls.some(
          (call) => call.callId === current.callId,
      )) {
        await stop();
      }
    } catch (error) {
      // Network and temporary function failures must not permanently remove
      // monitoring controls. Only an authoritative access error revokes them.
      if (isMonitorAccessRevoked(error)) {
        reviewerRef.current = false;
        setIsReviewer(false);
        setCalls([]);
        await stop();
      }
    }
  }, [enabled, stop]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const loadAccess = async () => {
      accessAttemptedAtRef.current = Date.now();
      try {
        const access = await listMonitorCalls({});
        const allowed = (access.data?.rooms || []).some(
            (room) => room.roomId === controls.roomId,
        );
        if (cancelled) return;
        reviewerRef.current = allowed;
        setIsReviewer(allowed);
        if (allowed) void refreshCalls();
      } catch (error) {
        if (!cancelled && isMonitorAccessRevoked(error)) {
          reviewerRef.current = false;
          setIsReviewer(false);
          setCalls([]);
        }
      }
    };
    void loadAccess();
    const syncMonitoring = () => {
      if (reviewerRef.current) {
        void refreshCalls();
        return;
      }
      if (Date.now() - accessAttemptedAtRef.current >= ACCESS_RETRY_MS) {
        void loadAccess();
      }
    };
    const timer = window.setInterval(syncMonitoring, 15000);
    const onVisible = () => {
      if (document.visibilityState === "visible") syncMonitoring();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, refreshCalls]);

  useEffect(() => {
    if (ownCallActive) void stop();
  }, [ownCallActive, stop]);

  useEffect(() => {
    const current = stateRef.current;
    if (!current) return;
    if (isMonitoredCallStillActive({
      queueEntries,
      callerId: current.callerId,
      connectedAt: current.connectedAt,
    })) return;
    void stop(true, "The monitored call has ended.");
  }, [queueEntries, stop]);

  useEffect(() => {
    const onPageHide = () => { void stop(); };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      void stop();
    };
  }, [stop]);

  const callByCaller = useMemo(() => {
    const lookup = new Map();
    calls.forEach((call) => {
      if (clean(call.callerId)) lookup.set(clean(call.callerId), call);
    });
    return lookup;
  }, [calls]);

  const findCall = useCallback((entry) => {
    const direct = callByCaller.get(clean(entry?.id));
    if (direct) return direct;
    return calls.find((call) =>
      clean(call.agentName).toLowerCase() ===
      clean(entry?.answeredBy).toLowerCase());
  }, [callByCaller, calls]);

  const canListen = useCallback((entry) =>
    isReviewer && !ownCallActive && Boolean(findCall(entry)),
  [findCall, isReviewer, ownCallActive]);

  const listen = useCallback(async (entry) => {
    if (ownCallActive) {
      showSnackbar(
          "End your current call before monitoring another agent.",
          "warning",
      );
      return;
    }
    const call = findCall(entry);
    if (!call || joiningCallId) return;
    if (stateRef.current?.callId === call.callId) {
      await stop();
      showSnackbar("Live monitoring stopped.");
      return;
    }
    if (stateRef.current) {
      showSnackbar(
          "Stop listening to the current call before opening another call.",
          "warning",
      );
      return;
    }
    setJoiningCallId(call.callId);
    try {
      const response = await createMonitorToken({
        roomId: controls.roomId,
        callId: call.callId,
      });
      const mediaRoom = new Room({
        adaptiveStream: true,
        dynacast: false,
      });
      const audioElements = [];
      const attachAudio = (track) => {
        if (track.kind !== Track.Kind.Audio) return;
        const audio = track.attach();
        audio.autoplay = true;
        audio.setAttribute("playsinline", "");
        audio.volume = monitorVolumeRef.current;
        document.body.append(audio);
        audioElements.push(audio);
        void audio.play().catch(() => {
          showSnackbar(
              "Tap Listen again if your browser blocks monitor audio.",
              "warning",
          );
        });
      };
      mediaRoom.on(RoomEvent.TrackSubscribed, attachAudio);
      mediaRoom.on(RoomEvent.TrackUnsubscribed, (track) => {
        track.detach().forEach((element) => element.remove());
      });
      mediaRoom.on(RoomEvent.Disconnected, () => {
        if (stateRef.current?.callId === call.callId) {
          void stop(false, "The monitored call has ended.");
        }
      });
      await mediaRoom.connect(
          response.data.url,
          response.data.token,
          {autoSubscribe: true},
      );
      const nextState = {
        callId: call.callId,
        callerId: clean(call.callerId) || clean(entry?.id),
        connectedAt: Number(call.connectedAt || entry?.timeAnswered || 0),
        monitorSessionId: response.data.monitorSessionId,
        room: mediaRoom,
        audioElements,
      };
      if (!isMonitoredCallStillActive({
        queueEntries: queueEntriesRef.current,
        callerId: nextState.callerId,
        connectedAt: nextState.connectedAt,
      })) {
        mediaRoom.disconnect();
        await closeMonitorSession({
          monitorSessionId: nextState.monitorSessionId,
        }).catch(() => {});
        showSnackbar("The monitored call has ended.");
        return;
      }
      stateRef.current = nextState;
      setListeningCallId(nextState.callId);
      setListeningCallerId(nextState.callerId);
      showSnackbar(`Listening to ${call.agentName || "agent"}'s call.`);
    } catch (error) {
      showSnackbar(
          error?.message || "The listen-only session could not start.",
          "error",
      );
    } finally {
      setJoiningCallId("");
    }
  }, [findCall, joiningCallId, ownCallActive, stop]);

  const getState = useCallback((entry) => {
    const call = findCall(entry);
    return {
      available: canListen(entry),
      joining: Boolean(call && joiningCallId === call.callId),
    listening: Boolean(call && listeningCallId === call.callId),
    };
  }, [canListen, findCall, joiningCallId, listeningCallId]);

  return {
    isReviewer,
    isMonitoring: Boolean(joiningCallId || listeningCallId),
    listeningCallerId,
    monitorVolume,
    setMonitorVolume,
    getState,
    listen,
    stop,
  };
}
